import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';

function isAbortError(err) {
  return (
    err?.code === 'ERR_CANCELED' ||
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError' ||
    err?.message === 'canceled'
  );
}

function badgeFrom(invitations, accountActions) {
  const unreadPending = (Array.isArray(invitations) ? invitations : []).filter((invitation) => {
    const status = String(invitation?.status || 'pending').toLowerCase();
    return status === 'pending' && !invitation?.readAt;
  }).length;
  const total = unreadPending + (Array.isArray(accountActions) ? accountActions.length : 0);
  return total > 0 ? { type: 'count', value: total } : null;
}

const HubNotificationsContext = createContext(null);

/**
 * Invitations and account-action counts for the hub bell.
 *
 * Each hub page used to fetch these for itself, so changing page rebuilt the
 * badge (and its dark loading chip) from scratch. The provider lives above
 * the router and keeps one snapshot for the session.
 */
export function HubNotificationsProvider({ children }) {
  const location = useLocation();
  const [invitations, setInvitations] = useState([]);
  const [accountActions, setAccountActions] = useState([]);
  const [invitationsReady, setInvitationsReady] = useState(false);
  const [accountActionsReady, setAccountActionsReady] = useState(false);
  const [actionLoadingIds, setActionLoadingIds] = useState(() => new Set());
  const loadedForTokenRef = useRef(null);

  const fetchInvitations = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await api.get('/api/invitations', {
        params: { status: 'pending', unread: true },
      });
      if (localStorage.getItem('token') !== token) return;
      setInvitations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (isAbortError(err) || localStorage.getItem('token') !== token) return;
      setInvitations([]);
    } finally {
      if (localStorage.getItem('token') === token) setInvitationsReady(true);
    }
  }, []);

  const fetchAccountActions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await api.get('/api/notifications/account-actions');
      if (localStorage.getItem('token') !== token) return;
      const actions = res?.data?.actions;
      setAccountActions(Array.isArray(actions) ? actions : []);
    } catch (err) {
      if (isAbortError(err) || localStorage.getItem('token') !== token) return;
      setAccountActions([]);
    } finally {
      if (localStorage.getItem('token') === token) setAccountActionsReady(true);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      loadedForTokenRef.current = null;
      setInvitations([]);
      setAccountActions([]);
      setInvitationsReady(false);
      setAccountActionsReady(false);
      setActionLoadingIds(new Set());
      return;
    }
    if (loadedForTokenRef.current === token) return;
    loadedForTokenRef.current = token;
    fetchInvitations();
    fetchAccountActions();
  }, [fetchInvitations, fetchAccountActions, location.pathname]);

  const withPending = useCallback(async (invitationId, work) => {
    if (!invitationId) return;
    setActionLoadingIds((prev) => new Set(prev).add(invitationId));
    try {
      await work();
    } finally {
      setActionLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(invitationId);
        return next;
      });
    }
  }, []);

  const acceptInvitation = useCallback(
    (invitationId) =>
      withPending(invitationId, async () => {
        await api.post(`/api/invitations/${invitationId}/accept`);
        setInvitations((prev) => prev.filter((invitation) => invitation?._id !== invitationId));
        fetchAccountActions();
      }),
    [fetchAccountActions, withPending]
  );

  const declineInvitation = useCallback(
    (invitationId) =>
      withPending(invitationId, async () => {
        try {
          await api.post(`/api/invitations/${invitationId}/decline`);
        } catch {
          await api.delete(`/api/invitations/${invitationId}`);
        }
        setInvitations((prev) => prev.filter((invitation) => invitation?._id !== invitationId));
        fetchAccountActions();
      }),
    [fetchAccountActions, withPending]
  );

  const markInvitationsReadLocally = useCallback((ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    const nowIso = new Date().toISOString();
    setInvitations((prev) =>
      Array.isArray(prev)
        ? prev.map((invitation) =>
            invitation && ids.includes(invitation._id)
              ? { ...invitation, readAt: invitation.readAt || nowIso }
              : invitation
          )
        : prev
    );
  }, []);

  const badge = useMemo(
    () => badgeFrom(invitations, accountActions),
    [invitations, accountActions]
  );

  const value = useMemo(
    () => ({
      invitations,
      accountActions,
      actionLoadingIds,
      badge,
      loading: !invitationsReady || !accountActionsReady,
      invitationsLoading: !invitationsReady,
      accountActionsLoading: !accountActionsReady,
      fetchInvitations,
      fetchAccountActions,
      acceptInvitation,
      declineInvitation,
      markInvitationsReadLocally,
    }),
    [
      invitations,
      accountActions,
      actionLoadingIds,
      badge,
      invitationsReady,
      accountActionsReady,
      fetchInvitations,
      fetchAccountActions,
      acceptInvitation,
      declineInvitation,
      markInvitationsReadLocally,
    ]
  );

  return (
    <HubNotificationsContext.Provider value={value}>{children}</HubNotificationsContext.Provider>
  );
}

export function useHubNotificationsStore() {
  const ctx = useContext(HubNotificationsContext);
  if (!ctx) {
    throw new Error('useHubNotifications must be used within a HubNotificationsProvider');
  }
  return ctx;
}

export default HubNotificationsContext;
